import React, { FC, useEffect, useState } from 'react'
import {
  Box,
  List,
  ListItem,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react'
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import { useCombobox } from 'downshift'

import { Input } from '../Input'

type Option = {
  name: string
  value: string
}

export type ComboboxProps = {
  id?: string
  isDisabled?: boolean
  onChange: (value: string | null) => void
  onValueUpdate?: () => void
  options: Option[]
  placeholder?: string
  value?: string | null
}

const filterOptionsThatMatchTypedInput = (
  typedInput: string | undefined,
  allOptions: Option[]
): Option[] => {
  return allOptions.filter(
    (option) =>
      !typedInput ||
      option.name.includes(typedInput) ||
      option.name.toLowerCase().includes(typedInput.toLowerCase())
  )
}

const itemToString = (item: Option | null | undefined) =>
  item ? item.name : ''

export const Combobox: FC<ComboboxProps> = ({
  isDisabled = false,
  onChange,
  onValueUpdate,
  options,
  placeholder = 'Select an option',
  value,
  id,
}) => {
  const [inputValue, setInputValue] = useState<string | undefined>(undefined)
  const items = filterOptionsThatMatchTypedInput(inputValue, options)
  const selectedItem = options.find((option) => option.value === value)
  useEffect(() => {
    if (!value) setInputValue(undefined)
  }, [value])

  const {
    getInputProps,
    getItemProps,
    getMenuProps,
    highlightedIndex,
    isOpen,
  } = useCombobox({
    itemToString,
    items,
    onInputValueChange: ({ inputValue: newInputValue }) => {
      setInputValue(newInputValue)
    },
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      onChange(newSelectedItem ? newSelectedItem.value : '')
      onValueUpdate?.()
    },
    selectedItem,
  })

  const inputProps = getInputProps({
    disabled: isDisabled,
    onKeyDown: (event) => {
      // Prevent user from deselecting the element. An element must be selected.
      if (event.key === 'Escape') {
        // @ts-ignore-next-line
        // `nativeElement` inherits from React; the library type is wrong.
        event.nativeEvent.preventDownshiftDefault = true
      }
    },
  })

  return (
    <Box position="relative" width="100%" id={id}>
      <InputGroup>
        <Input
          placeholder={placeholder}
          {...inputProps}
          // When the user isn't interacting with the combobox, show the selected item instead of whatever the user typed so there's no confusion about the current state of the form.
          value={isOpen ? inputValue : itemToString(selectedItem)}
        />
        <InputRightElement
          pointerEvents="none"
          top="50%"
          transform="translateY(-50%)"
        >
          {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </InputRightElement>
      </InputGroup>
      <List
        boxShadow="md"
        maxHeight="20ex"
        overflowY="scroll"
        position="absolute"
        width="100%"
        zIndex="dropdown"
        {...(!isOpen && { display: 'none' })}
        {...getMenuProps()}
      >
        {isOpen &&
          items.map((item, index) => (
            <ListItem
              key={item.value}
              layerStyle="softestWhite"
              px="lg"
              py="sm"
              {...(highlightedIndex === index && {
                layerStyle: 'softerOrange',
              })}
              {...(selectedItem === item && { layerStyle: 'softBlack' })}
              {...getItemProps({ item, index })}
            >
              {item.name}
            </ListItem>
          ))}
      </List>
    </Box>
  )
}
